<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import countries from 'shared/constants/countries.js';
import { useLocale } from 'forms/composables/useLocale';

const { t } = useLocale();

const props = defineProps({
  modelValue: { type: String, default: '' },
  error: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const search = ref('');
const rootRef = ref(null);
const searchRef = ref(null);

const selected = computed(
  () => countries.find(c => c.id === props.modelValue) || null
);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return countries;
  return countries.filter(
    c =>
      c.name.toLowerCase().includes(q) ||
      c.dial_code.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q)
  );
});

const triggerClasses = computed(() => {
  const base =
    'flex items-center justify-between gap-1 shrink-0 w-28 rounded-lg border px-2.5 py-2.5 text-sm text-slate-800 bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0';
  return props.error
    ? `${base} border-red-300 focus:border-red-400 focus:ring-red-200`
    : `${base} border-slate-300 focus:border-woot-400 focus:ring-woot-200`;
});

function onDocPointer(event) {
  if (rootRef.value && !rootRef.value.contains(event.target)) close();
}

function close() {
  if (!open.value) return;
  open.value = false;
  document.removeEventListener('mousedown', onDocPointer);
}

function toggle() {
  if (open.value) {
    close();
    return;
  }
  open.value = true;
  search.value = '';
  document.addEventListener('mousedown', onDocPointer);
  nextTick(() => searchRef.value?.focus());
}

function select(country) {
  emit('update:modelValue', country.id);
  close();
}

onBeforeUnmount(() => document.removeEventListener('mousedown', onDocPointer));
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      :class="triggerClasses"
      aria-label="Country code"
      @click="toggle"
    >
      <span class="flex items-center gap-1 min-w-0">
        <span v-if="selected" class="text-base leading-none">{{
          selected.emoji
        }}</span>
        <span class="truncate">{{ selected ? selected.dial_code : '+' }}</span>
      </span>
      <svg
        class="w-3.5 h-3.5 text-slate-400 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <div
      v-if="open"
      class="absolute z-20 mt-1 w-64 max-w-[80vw] rounded-lg border border-slate-200 bg-white shadow-lg"
    >
      <div class="p-2 border-b border-slate-100">
        <input
          ref="searchRef"
          v-model="search"
          type="text"
          :placeholder="t('FIELD_COUNTRY_SEARCH')"
          class="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-200 focus:border-woot-400"
        />
      </div>
      <ul class="max-h-56 overflow-y-auto py-1">
        <li
          v-for="country in filtered"
          :key="country.id"
          class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
          :class="
            country.id === modelValue ? 'bg-woot-50 text-woot-700' : 'text-slate-700'
          "
          @click="select(country)"
        >
          <span class="text-base leading-none">{{ country.emoji }}</span>
          <span class="truncate flex-1">{{ country.name }}</span>
          <span class="text-xs text-slate-400">{{ country.dial_code }}</span>
        </li>
        <li
          v-if="filtered.length === 0"
          class="px-3 py-3 text-sm text-center text-slate-400"
        >
          {{ t('FIELD_COUNTRY_EMPTY') }}
        </li>
      </ul>
    </div>
  </div>
</template>
